import { CoreButton, CoreCheckbox, CoreInput, CoreSelect } from '@components'
import CoreLoader from '@components/CoreLoader'
import HintIcon from '@images/icons/hint_select_role_icon.svg?react'
import BankMetadataFieldInput from '@recipients/components/BankMetadataFieldInput'
import { BANK_RECIPIENT_ERROR_SUBSTITUTES } from '@views/recipients/constants'
import { useCountries, useCurrencies, useEditBankAccountQry, useMerchantQry } from 'mmfintech-backend-api'
import type { BankFieldMetadata, SelectOptions } from 'mmfintech-backend-api/types'
import { isValidArray, tr } from 'mmfintech-commons'
import { ErrorDisplay } from 'mmfintech-portal-commons'
import { useEffect, useMemo, useState } from 'react'
import ReactTooltip from 'react-tooltip'

const CreateBankRecipient = ({ currencyCode, foreignId, personal, onSuccess }) => {
  const { merchant } = useMerchantQry()

  const [filteredCurrencies, setFilteredCurrencies] = useState<SelectOptions>(null)

  const { getCurrencyType, currencies, currencyOptions } = useCurrencies()
  const { countryOptions /*, countryOptionsFiltered*/ } = useCountries('WITHDRAWAL')

  const {
    // step,
    // setStep,
    formValues,
    countryCode,
    setCountryCode,
    bankMetadata,
    bankMetadataError,
    bankMetadataFetching,
    accountTypeOptions,
    // documentTypes,
    documentTypesOptions,
    handleSubmit,
    providedBanks,
    isFieldVisible,
    saveRecipientError,
    saveRecipientLoading
  } = useEditBankAccountQry({
    // accountId,
    foreignId,
    // recipientId,
    // currencyCode,
    enableProvidedBanks: true,
    onSuccess: (response: any) => onSuccess(response?.paymentInstrumentId)
  })

  // const providedBanksOptions = () =>
  //   isValidArray(providedBanks)
  //     ? providedBanks
  //       .filter((v: BankOut) => !!v.code?.length)
  //       .map((bank: BankOut) => ({ value: bank.id, label: bank.name }))
  //     : []
  //
  // const handleBankChange = (bankId: string): void => {
  //   const found = providedBanks?.find((bank: BankOut) => bank.id === Number(bankId))
  //   if (found) {
  //     formValues.setValue('bankName', found.name)
  //     formValues.setValue('bankCode', found.code)
  //   }
  // }
  //
  // const bankRecipientFormDetails = useMemo(() => {
  //   return splitBankRecipientData(formValues)
  // }, [formValues])
  //
  // const showBanksDropDown = countryCode !== 'BRA' && isValidArray(providedBanks)

  const handleClick = () => formValues.setValue('personal', !formValues.getValue('personal'))

  const metadataCustomError = useMemo(() => {
    if (bankMetadataError && formValues.getValue('currency') && countryCode) {
      return {
        error: `${tr(
          'FRONTEND.RECIPIENTS.METADATA.CUSTOM_ERROR_WITHDRAW',
          'Cannot withdraw {{CURRENCY}} in {{COUNTRY}}',
          {
            CURRENCY: formValues.getValue('currency'),
            COUNTRY: countryOptions?.find(x => x.value === countryCode)?.label
          }
        )}`
      }
    }
    return null
  }, [bankMetadataError])

  const validMetadataCategory = (field: BankFieldMetadata, category: string): boolean =>
    field.code !== 'COUNTRY_CODE' && field.code !== 'HOLDER_NAME' && field.fieldCategory === category

  useEffect(() => {
    formValues.setValue('personal', foreignId ? false : personal)
  }, [])

  useEffect(() => {
    const options = currencyOptions(
      c => (c.currencyCode === currencyCode || c.currencyCode === merchant?.primaryCurrency) && c.type === 'FIAT'
    )
    if (isValidArray(options)) {
      setFilteredCurrencies(options)
      formValues.setValue('currency', getCurrencyType(currencyCode) === 'FIAT' ? currencyCode : options[0].value)
    } else {
      setFilteredCurrencies(null)
      formValues.setValue('currency', '')
    }
  }, [currencyCode, currencies])

  useEffect(() => {
    if (saveRecipientError) {
      formValues.handleErrors(saveRecipientError, BANK_RECIPIENT_ERROR_SUBSTITUTES)
    }
  }, [saveRecipientError])

  return (
    <>
      <div className='group-inputs'>
        <CoreSelect
          type='currency'
          data-test='currency'
          label={tr('FRONTEND.ACCOUNTS.CREATE.CURRENCY_LABEL', 'Currency')}
          options={filteredCurrencies}
          value={formValues.getValue('currency')}
          onChange={(_, val: any) => {
            formValues.setValue('currency', val.value)
          }}
        />
        <CoreSelect
          type='country'
          value={countryCode}
          options={countryOptions}
          data-test='country-code'
          label={tr('FRONTEND.RECIPIENTS.LABELS.BANK_COUNTRY', 'Country')}
          onChange={(_: string, value: string) => setCountryCode(value)}
          required
        />
      </div>

      <CoreInput
        type='text'
        data-test='account-name'
        // maxLength={100}
        label={tr('FRONTEND.RECIPIENTS.LABELS.ACCOUNT_NAME', 'Recipient name')}
        // tooltip={tr(formValues.getTooltip('name'), '')}
        {...formValues.registerInput('name')}
        autoComplete='off'
      />

      {bankMetadataFetching /*&& documentTypesFetching*/ && <CoreLoader />}

      {isValidArray(bankMetadata) &&
        formValues &&
        bankMetadata
          .filter(meta => validMetadataCategory(meta, 'BANK_DETAILS') && isFieldVisible(meta))
          .map(field => (
            <BankMetadataFieldInput
              key={field.code}
              field={field}
              formValues={formValues}
              countryCode={countryCode}
              accountTypes={accountTypeOptions}
              providedBanks={providedBanks}
              documentTypesOptions={documentTypesOptions}
            />
          ))}

      <CoreInput
        type='text'
        maxLength={100}
        data-test='description'
        label={tr('FRONTEND.RECIPIENTS.LABELS.DESCRIPTION', 'Description')}
        {...formValues.registerInput('description')}
        autoComplete='off'
      />

      {isValidArray(bankMetadata) &&
        bankMetadata
          .filter(meta => validMetadataCategory(meta, 'RECIPIENT_DETAILS') && isFieldVisible(meta))
          .map(field => (
            <BankMetadataFieldInput
              key={field.code}
              field={field}
              formValues={formValues}
              countryCode={countryCode}
              accountTypes={accountTypeOptions}
              providedBanks={providedBanks}
              documentTypesOptions={documentTypesOptions}
            />
          ))}

      <CoreCheckbox
        className='form-checkbox'
        type='check'
        checked={Boolean(formValues.getValue('personal'))}
        value='personal'
        onClick={handleClick}
        disabled={foreignId || personal}
        label={tr('FRONTEND.RECIPIENTS.LABELS.BANK_PERSONAL_ACCOUNT', 'This is my personal account')}
        hint={
          <span
            style={{ marginLeft: '8px' }}
            data-for='personal-account-tooltip'
            data-tip={tr(
              'FRONTEND.RECIPIENTS.LABELS.BANK_PERSONAL_ACCOUNT_TOOLTIP',
              'Personal accounts are your own external accounts and are only visible to you,<br/>administrators, and authorized departments within a company account.'
            )}
          >
            <HintIcon width={16} height={16} />
          </span>
        }
      />
      <ReactTooltip id='personal-account-tooltip' padding='8px 6px' html={true} aria-haspopup='true' />

      <ErrorDisplay error={[saveRecipientError, metadataCustomError]} />

      <CoreButton
        type='button'
        data-test='save-button'
        variation='elevated'
        size='large'
        fullWidth
        text={tr('FRONTEND.RECIPIENTS.EDIT.BUTTON_SAVE_RECIPIENT', 'Save recipient')}
        className='save-button'
        onClick={() => void handleSubmit()}
        isLoading={saveRecipientLoading}
      />
    </>
  )
}

export default CreateBankRecipient
